import * as React from 'react';
import { Card, CardContent, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';

/** One provider-run web tool as edited (strings while typing); stored in the agent's `params.serverTools`. */
export interface WebToolForm {
    enabled: boolean;
    maxUses: string;
    /** none = no restriction; allow = only these domains; block = never these domains (the API takes one list). */
    domainMode: 'none' | 'allow' | 'block';
    domains: string;
    /** web_fetch only: the most tokens one fetched page may add. */
    maxContentTokens: string;
    /** The model filters results with code before they reach its context (Claude 4.6+); off = the basic tool. */
    dynamicFiltering: boolean;
}

export interface WebToolsForm {
    search: WebToolForm;
    fetch: WebToolForm;
}

const OFF: WebToolForm = { enabled: false, maxUses: '', domainMode: 'none', domains: '', maxContentTokens: '', dynamicFiltering: true };
export const EMPTY_WEB_TOOLS: WebToolsForm = { search: OFF, fetch: OFF };

const TYPES = { search: 'web_search', fetch: 'web_fetch' } as const;

function toolForm(entry: Record<string, unknown> | undefined): WebToolForm {
    if (!entry) return OFF;
    const list = (key: string) => (Array.isArray(entry[key]) ? (entry[key] as unknown[]).map(String) : []);
    const allowed = list('allowedDomains');
    const blocked = list('blockedDomains');
    return {
        enabled: true,
        maxUses: entry.maxUses != null ? String(entry.maxUses) : '',
        domainMode: allowed.length > 0 ? 'allow' : blocked.length > 0 ? 'block' : 'none',
        domains: (allowed.length > 0 ? allowed : blocked).join('\n'),
        maxContentTokens: entry.maxContentTokens != null ? String(entry.maxContentTokens) : '',
        dynamicFiltering: entry.dynamicFiltering !== false,
    };
}

export function webToolsFromParams(value: unknown): WebToolsForm {
    const entries = Array.isArray(value) ? value.filter(v => v && typeof v === 'object') as Record<string, unknown>[] : [];
    const find = (type: string) => entries.find(e => String(e.type ?? '').toLowerCase() === type);
    return { search: toolForm(find(TYPES.search)), fetch: toolForm(find(TYPES.fetch)) };
}

/** The `params.serverTools` value (empty when both are off), or an error message. */
export function webToolsToParams(form: WebToolsForm): { value: Record<string, unknown>[]; error: string | null } {
    const value: Record<string, unknown>[] = [];
    for (const key of ['search', 'fetch'] as const) {
        const t = form[key];
        if (!t.enabled) continue;
        const label = key === 'search' ? 'Web search' : 'Web fetch';
        const entry: Record<string, unknown> = { type: TYPES[key], dynamicFiltering: t.dynamicFiltering };
        if (t.maxUses.trim()) {
            const n = Number(t.maxUses);
            if (!Number.isInteger(n) || n < 1 || n > 50) return { value, error: `${label}: max uses must be 1–50.` };
            entry.maxUses = n;
        }
        const domains = t.domains.split(/[\s,]+/).map(d => d.trim()).filter(Boolean);
        if (t.domainMode !== 'none') {
            if (domains.length === 0) return { value, error: `${label}: list at least one domain, or choose no restriction.` };
            const bad = domains.find(d => d.includes('://') || !d.includes('.'));
            if (bad) return { value, error: `${label}: '${bad}' is not a bare domain (e.g. bbc.co.uk, no https://).` };
            entry[t.domainMode === 'allow' ? 'allowedDomains' : 'blockedDomains'] = domains;
        }
        if (key === 'fetch' && t.maxContentTokens.trim()) {
            const n = Number(t.maxContentTokens);
            if (!Number.isInteger(n) || n < 1000) return { value, error: 'Web fetch: max content tokens must be at least 1000.' };
            entry.maxContentTokens = n;
        }
        value.push(entry);
    }
    return { value, error: null };
}

function WebToolRow({ title, help, tool, fetch, onChange }: {
    title: string; help: string; tool: WebToolForm; fetch: boolean; onChange: (t: WebToolForm) => void;
}) {
    const patch = (p: Partial<WebToolForm>) => onChange({ ...tool, ...p });
    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={2}>
                <FormControlLabel control={<Switch checked={tool.enabled} onChange={e => patch({ enabled: e.target.checked })} />}
                    label={<Typography variant="subtitle2">{title}</Typography>} />
                <Typography variant="caption" color="text.secondary">{help}</Typography>
            </Stack>
            {tool.enabled && (
                <Grid container spacing={2}>
                    <Grid item xs={6} md={2}>
                        <TextField size="small" fullWidth label="Max uses per call" value={tool.maxUses} placeholder="no limit"
                            onChange={e => patch({ maxUses: e.target.value.replace(/[^\d]/g, '') })} />
                    </Grid>
                    {fetch && (
                        <Grid item xs={6} md={2}>
                            <TextField size="small" fullWidth label="Max tokens per page" value={tool.maxContentTokens} placeholder="no limit"
                                onChange={e => patch({ maxContentTokens: e.target.value.replace(/[^\d]/g, '') })} />
                        </Grid>
                    )}
                    <Grid item xs={12} md={3}>
                        <TextField select size="small" fullWidth label="Domains" value={tool.domainMode}
                            onChange={e => patch({ domainMode: e.target.value as WebToolForm['domainMode'] })}>
                            <MenuItem value="none">No restriction</MenuItem>
                            <MenuItem value="allow">Only these domains</MenuItem>
                            <MenuItem value="block">Never these domains</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={fetch ? 3 : 5}>
                        <TextField size="small" fullWidth multiline minRows={1} maxRows={6} label="Domain list" value={tool.domains}
                            disabled={tool.domainMode === 'none'} placeholder="bbc.co.uk, uefa.com"
                            helperText="Bare domains, one per line or comma-separated; subdomains are included." onChange={e => patch({ domains: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControlLabel control={<Switch checked={tool.dynamicFiltering} onChange={e => patch({ dynamicFiltering: e.target.checked })} />}
                            label={<Typography variant="body2">Dynamic filtering</Typography>} />
                    </Grid>
                </Grid>
            )}
        </Stack>
    );
}

/**
 * Agent editor › Web tools: web search and web fetch, run by Anthropic during the model call (not MCP tools). Saved with
 * the agent in `params.serverTools`; only Anthropic models run them (a DeepSeek model or fallback ignores them).
 */
export default function AgentWebTools({ value, onChange }: { value: WebToolsForm; onChange: (value: WebToolsForm) => void }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="subtitle1">Web tools</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Let the model research on the web while it answers. Web search costs $10 per 1,000 searches on top of tokens
                    (counted in usage and budgets); web fetch reads pages whose URL is already in the conversation and costs tokens only.
                    Dynamic filtering lets the model filter results with code before they reach its context (fewer tokens; Claude 4.6
                    and later) — turn it off if a model rejects it.
                </Typography>
                <Stack gap={2}>
                    <WebToolRow title="Web search" help="Searches the web; results come with sources." tool={value.search} fetch={false}
                        onChange={search => onChange({ ...value, search })} />
                    <WebToolRow title="Web fetch" help="Reads a page found in search results or given in the prompt." tool={value.fetch} fetch
                        onChange={fetch => onChange({ ...value, fetch })} />
                </Stack>
            </CardContent>
        </Card>
    );
}
